import {
  type Accessor,
  createContext,
  createEffect,
  createMemo,
  createSignal,
  onCleanup,
  type ParentComponent,
  useContext,
} from "solid-js";
import { useDownloadAtsReport } from "@/domains/company/report/api/offer-report";
import { ReportFiltersDialog } from "@/domains/company/report/components/report-filters-dialog";
import { ReportGenerationDialog } from "@/domains/company/report/components/report-generation-dialog";
import { ReportGenerationIndicator } from "@/domains/company/report/components/report-generation-indicator";
import {
  isUnfiltered,
  type ReportFilters,
  toFiltersPayload,
} from "@/domains/company/report/types";
import {
  MIN_DIALOG_VISIBLE_MS,
  PROGRESS_FILL_MS,
  PROGRESS_HOLD_AT_FULL_MS,
  phaseAt,
  progressAt,
} from "@/domains/company/report/utils/report-phases";
import type { JobOfferV2WithCompanyId } from "@/domains/job-offer/types";
import { usePostHog } from "@/hooks/usePostHog";

/** Where the download was started from — becomes the PostHog funnel property. */
export type ReportEntryPoint = "ats" | "ats_detail";

export type ReportGenerationContextValue = {
  /** The offer currently generating, or `null` when idle. */
  activeOffer: Accessor<JobOfferV2WithCompanyId | null>;
  /** Route the generation was started from — drives dialog vs indicator. */
  originPath: Accessor<string | null>;
  /** Index into `REPORT_PHASES` for the narrated step on screen. */
  phaseIndex: Accessor<number>;
  /** Loader bar fill, 0→1. Reaches 1 only once the PDF is actually in hand. */
  progress: Accessor<number>;
  /** True while a generation is in flight for this offer. */
  isPendingFor: (jobOfferId: string) => boolean;
  /**
   * Apre la scelta della platea per questa offerta. La generazione parte solo
   * alla conferma: e' li' che si conosce il conteggio reale, quindi e' li' che
   * il cap puo' essere annunciato con un numero vero.
   *
   * `prefill` arriva dalla pagina che ha il filtro di stato nell'URL — il
   * provider sta sopra il router e non puo' leggerlo da solo.
   */
  start: (
    offer: JobOfferV2WithCompanyId | null | undefined,
    entryPoint: ReportEntryPoint,
    originPath: string,
    prefill?: Partial<ReportFilters>,
  ) => void;
  /** L'offerta in attesa di conferma nel dialog filtri, o `null`. */
  pendingOffer: Accessor<JobOfferV2WithCompanyId | null>;
  /** Prefill del dialog, dedotto dai filtri della pagina di provenienza. */
  pendingPrefill: Accessor<Partial<ReportFilters> | undefined>;
  confirmFilters: (filters: ReportFilters, candidateCount: number) => void;
  dismissFilters: () => void;
  /** True while the user has navigated away from `originPath`. */
  isMinimized: Accessor<boolean>;
  /** Set by `ReportGenerationRouteSync` from inside the router. */
  setCurrentPath: (path: string) => void;
  minimize: () => void;
  restore: () => void;
};

const ReportGenerationContext = createContext<ReportGenerationContextValue>();

export const useReportGeneration = (): ReportGenerationContextValue => {
  const ctx = useContext(ReportGenerationContext);
  if (!ctx) {
    throw new Error(
      "useReportGeneration must be used within a ReportGenerationProvider",
    );
  }
  return ctx;
};

/**
 * Owns the ATS report generation: the request, the narrated phase clock, the
 * over-cap gate and the funnel tracking.
 *
 * **Mounted above the router on purpose.** The previous home for this state was
 * a hook inside the two ATS route components, which meant navigating away
 * unmounted it and abandoned a generation the user had already waited ~40s for.
 * Living in the provider tree (beside `CompanyProvider` / `VisitedJobProvider`)
 * the request survives navigation and still lands its PDF and toast, wherever
 * the user has gone. See `brain/specs/company/report-generation-loader/`.
 *
 * Idle cost is nil: no query, no timer, nothing runs until `start` is called.
 */
export const ReportGenerationProvider: ParentComponent = (props) => {
  const { capture } = usePostHog();
  const mutation = useDownloadAtsReport();

  const [activeOffer, setActiveOffer] =
    createSignal<JobOfferV2WithCompanyId | null>(null);
  const [originPath, setOriginPath] = createSignal<string | null>(null);
  const [pendingOffer, setPendingOffer] =
    createSignal<JobOfferV2WithCompanyId | null>(null);
  const [pendingPrefill, setPendingPrefill] = createSignal<
    Partial<ReportFilters> | undefined
  >(undefined);
  /**
   * Candidati reali della generazione in corso, per la stima di durata.
   *
   * Non si usa piu' `applications_count`: era gonfiata, e narrare 5 minuti
   * mentre il backend ne costruisce 50 in ~50s e' un'attesa inventata.
   */
  const [activeCount, setActiveCount] = createSignal<number | undefined>(
    undefined,
  );
  const [isMinimized, setIsMinimized] = createSignal(false);
  const [elapsed, setElapsed] = createSignal(0);
  /* True from the moment the request settles. It is what completes the bar —
     the clock never does — so it has to be state the dialog can read, not a
     local variable inside the mutation callback. */
  const [arrived, setArrived] = createSignal(false);
  // Fed by `ReportGenerationRouteSync` from inside the router: this provider
  // sits above it, where `useLocation()` is not available.
  const [currentPath, setCurrentPath] = createSignal<string | null>(null);

  let ticker: ReturnType<typeof setInterval> | undefined;
  let startedAt = 0;
  /** Survives the over-cap confirm step so the funnel keeps the right source. */
  let pendingEntryPoint: ReportEntryPoint = "ats";
  /** Set when the user reopens the dialog from the indicator, off the origin
   * route — lets an explicit choice outrank the route-driven default. */
  let restoredAwayFromOrigin = false;

  const stopTicker = () => {
    if (ticker !== undefined) {
      clearInterval(ticker);
      ticker = undefined;
    }
  };
  onCleanup(stopTicker);

  const phaseIndex = createMemo(() => phaseAt(elapsed(), activeCount()));

  const progress = createMemo(() =>
    progressAt(elapsed(), activeCount(), arrived()),
  );

  // The dialog belongs to the route that opened it; anywhere else the loader
  // shows as the indicator. Navigating back to the origin restores the dialog,
  // so the two surfaces are one state seen from two places — not a mode the
  // user has to manage.
  //
  // `restore()` (clicking the indicator) is an explicit override of that rule:
  // without the flag this effect would re-minimize on the very next run and the
  // dialog would close again the instant it opened.
  createEffect(() => {
    const origin = originPath();
    const path = currentPath();
    if (!activeOffer() || !origin || path === null) return;
    if (path === origin) {
      restoredAwayFromOrigin = false;
      setIsMinimized(false);
      return;
    }
    setIsMinimized(!restoredAwayFromOrigin);
  });

  /**
   * Settles the loader: the bar completes, the user sees it full, then the
   * dialog goes and the PDF lands.
   *
   * The order matters and is the whole point of the bar. `setArrived(true)`
   * drives the fill to 100%; only after it has been drawn ({@link
   * PROGRESS_FILL_MS}) and held long enough to read ({@link
   * PROGRESS_HOLD_AT_FULL_MS}) does the dialog close. Closing on the response
   * alone would show the bar vanishing mid-fill.
   *
   * The minimum-visible floor still applies underneath: with a warm cache the
   * request answers in ~180ms, and without it the dialog would appear and
   * vanish inside a couple of frames — read as a glitch, not as success.
   */
  const finish = (onClosed?: () => void) => {
    setArrived(true);

    const visibleFor = Date.now() - startedAt;
    const completion = PROGRESS_FILL_MS + PROGRESS_HOLD_AT_FULL_MS;
    const remaining = Math.max(MIN_DIALOG_VISIBLE_MS - visibleFor, completion);

    setTimeout(() => {
      stopTicker();
      setActiveOffer(null);
      setOriginPath(null);
      setIsMinimized(false);
      setElapsed(0);
      setArrived(false);
      // Azzerato con il resto: lasciarlo farebbe stimare la generazione
      // successiva sulla platea di quella precedente.
      setActiveCount(undefined);
      onClosed?.();
    }, remaining);
  };

  const run = (
    offer: JobOfferV2WithCompanyId,
    entryPoint: ReportEntryPoint,
    path: string,
    filters: ReportFilters,
  ) => {
    setActiveOffer(offer);
    setOriginPath(path);
    restoredAwayFromOrigin = false;
    setIsMinimized(false);
    setElapsed(0);
    setArrived(false);
    startedAt = Date.now();
    stopTicker();
    ticker = setInterval(() => setElapsed(Date.now() - startedAt), 200);

    const filtered = !isUnfiltered(filters);
    capture("submit_report_generation", {
      entry_point: entryPoint,
      job_offer_id: offer.id,
      filters_applied: filtered,
      initiator: filters.initiator,
      status_count:
        filters.initiator === "company"
          ? filters.proposalStates.length
          : filters.statuses.length,
      contacted_only: filters.contactedOnly,
    });

    mutation.mutate(
      {
        jobOfferId: offer.id,
        filters: toFiltersPayload(filters),
      },
      {
        onSuccess: (result) => {
          capture("create_report_success", {
            entry_point: entryPoint,
            job_offer_id: offer.id,
            cache_hit: result.cacheHit,
            degraded: result.degraded,
            degraded_count: result.degradedCount,
            filters_applied: filtered,
          });
          finish();
        },
        onError: () => {
          capture("fail_report_generation", {
            entry_point: entryPoint,
            job_offer_id: offer.id,
            reason: "error",
          });
          finish();
        },
      },
    );
  };

  const start: ReportGenerationContextValue["start"] = (
    offer,
    entryPoint,
    path,
    prefill,
  ) => {
    if (!offer) return;
    // One generation at a time: the modal makes a second start unreachable in
    // practice, but a stray call must not strand the first one's state.
    if (activeOffer()) return;

    // La decisione si sposta tutta nel dialog: qui non si conosce la platea
    // reale, e il vecchio gate la deduceva da `applications_count`, che e'
    // gonfiata. Il dialog interroga il conteggio vero e puo' quindi annunciare
    // il cap con un numero in cui il recruiter puo' credere.
    setPendingOffer(offer);
    setPendingPrefill(prefill);
    setOriginPath(path);
    // Entry point has to survive the confirm step to keep the funnel honest.
    pendingEntryPoint = entryPoint;
  };

  const confirmFilters = (filters: ReportFilters, candidateCount: number) => {
    const offer = pendingOffer();
    const path = originPath();
    setPendingOffer(null);
    // Il conteggio arriva dal dialog, che l'ha gia' chiesto al backend: e' la
    // platea reale del documento, quindi la stima di durata parte da li'.
    setActiveCount(candidateCount);
    if (offer && path) run(offer, pendingEntryPoint, path, filters);
  };

  const dismissFilters = () => {
    setPendingOffer(null);
    setPendingPrefill(undefined);
    setOriginPath(null);
  };

  const value: ReportGenerationContextValue = {
    activeOffer,
    originPath,
    phaseIndex,
    progress,
    isPendingFor: (jobOfferId) => activeOffer()?.id === jobOfferId,
    start,
    pendingOffer,
    pendingPrefill,
    confirmFilters,
    dismissFilters,
    isMinimized,
    setCurrentPath,
    minimize: () => {
      restoredAwayFromOrigin = false;
      setIsMinimized(true);
    },
    restore: () => {
      restoredAwayFromOrigin = true;
      setIsMinimized(false);
    },
  };

  return (
    <ReportGenerationContext.Provider value={value}>
      {props.children}
      {/* Rendered here, above the router, so both surfaces are reachable from
          any route the user wanders to while the report is generating. */}
      <ReportGenerationDialog />
      <ReportGenerationIndicator />
      {/* Montato qui e non nelle route: `ReportOverCapDialog` viveva in due
          copie, una per pagina, e ogni superficie nuova avrebbe ereditato
          quella duplicazione. */}
      <ReportFiltersDialog
        offer={pendingOffer()}
        prefill={pendingPrefill()}
        onConfirm={confirmFilters}
        onDismiss={dismissFilters}
      />
    </ReportGenerationContext.Provider>
  );
};
